import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type { BudgetReport, DepartmentBudget, SaveDepartmentBudget } from '../types';

/**
 * Department budgets and the budget-versus-committed report (#19). Budget writes invalidate the
 * whole dashboard prefix, which holds the report and the dashboard card.
 */

const fetchBudgetReport = async (fiscalYear?: number): Promise<BudgetReport> =>
  api.get<BudgetReport>(
    fiscalYear === undefined ? '/dashboard/budget-vs-committed' : `/dashboard/budget-vs-committed?fiscalYear=${fiscalYear}`
  );

/** The report for a fiscal year; undefined means the current one (the API answers with which). */
export const useBudgetReport = (fiscalYear?: number) =>
  useQuery({
    queryKey: queryKeys.dashboard.budgetReport(fiscalYear),
    queryFn: () => fetchBudgetReport(fiscalYear),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });

interface CreateArgs {
  departmentId: string;
  data: SaveDepartmentBudget;
}

interface UpdateArgs extends CreateArgs {
  id: string;
}

export const useCreateBudget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ departmentId, data }: CreateArgs) =>
      api.post<DepartmentBudget>(`/departments/${departmentId}/budgets`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  });
};

export const useUpdateBudget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ departmentId, id, data }: UpdateArgs) =>
      api.put<DepartmentBudget>(`/departments/${departmentId}/budgets/${id}`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  });
};

export const useDeleteBudget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ departmentId, id }: { departmentId: string; id: string }) =>
      api.delete<void>(`/departments/${departmentId}/budgets/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  });
};
